// (paper-20261027) Manual paper trading — longs only, NEVER sends a transaction.
// (papernofee-20261027) Straight trade, no fees: PnL = (exit - entry) * qty.
// Buys fill at the last BUY print (the offer), sells at the last SELL print
// (the bid); the caller passes that mcap (falls back to the last trade).
// Units are "mcap units": tokens ∝ SOL / mcap, so price ratios are exact
// without needing supply.

const KEY = 'cheddar_paper_v1'
const HIST_MAX = 100
export const PAPER_START_SOL = 10

// (paperwallet-20261027) Paper is a wallet in the wallet menu. While it is the
// active wallet every BUY/SELL routes here, and the signers refuse to sign.
const ACTIVE_KEY = 'solcheddar_paper_active'   // '1' while Paper is the active wallet
const ADDED_KEY = 'solcheddar_paper_added'     // '1' once Paper was added via "+"
export const PAPER_EVENT = 'solcheddar-paper-changed'
export function isPaperActive() { try { return localStorage.getItem(ACTIVE_KEY) === '1' } catch { return false } }
export function isPaperAdded() { try { return localStorage.getItem(ADDED_KEY) === '1' } catch { return false } }
export function setPaperActive(on) {
  try {
    if (on) { localStorage.setItem(ACTIVE_KEY, '1'); localStorage.setItem(ADDED_KEY, '1') }
    else localStorage.removeItem(ACTIVE_KEY)
  } catch {}
  emit()
}
function emit() { try { window.dispatchEvent(new CustomEvent(PAPER_EVENT)) } catch {} }

function load() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (s && typeof s === 'object') return {
      pos: nofee(s.pos || {}), hist: Array.isArray(s.hist) ? s.hist : [],
      cash: Number.isFinite(s.cash) ? s.cash : PAPER_START_SOL,
      realized: Number.isFinite(s.realized) ? s.realized : 0,
    }
  } catch {}
  return { pos: {}, hist: [], cash: PAPER_START_SOL, realized: 0 }
}
// Positions opened under the old 3% fee: re-derive qty as SOL in / avg fill,
// so their PnL reads as a straight trade too. Idempotent (flag nf).
function nofee(pos) {
  for (const k in pos) {
    const p = pos[k]
    if (p && !p.nf && p.entryMcap > 0 && p.costSol > 0) { p.units = p.costSol / p.entryMcap; p.nf = 1 }
  }
  return pos
}
function save(s) { try { localStorage.setItem(KEY, JSON.stringify(s)) } catch {} emit() }

// Paper SOL balance (cash, open positions excluded) and net realized PnL.
export function paperAccount() {
  const s = load()
  return { cash: s.cash, realized: s.realized, open: Object.keys(s.pos).length }
}

export function paperPosition(token) {
  return (token && load().pos[token]) || null
}

// PnL if the position were sold right now at mcap (no fees).
export function paperOpenPnl(pos, mcap) {
  if (!pos || !(mcap > 0)) return null
  const value = pos.units * mcap
  const pnl = value - pos.costSol
  return { pnl, pct: pos.costSol > 0 ? (pnl / pos.costSol) * 100 : 0 }
}

export function paperBuy(token, sol, mcap) {
  if (!token) throw new Error('No token selected')
  if (!(sol > 0)) throw new Error('Set a SOL amount')
  if (!(mcap > 0)) throw new Error('No live price yet')
  const s = load()
  if (sol > s.cash + 1e-9) throw new Error(`Paper balance too low (${s.cash.toFixed(3)} SOL)`)
  s.cash -= sol
  const p = s.pos[token] || { units: 0, costSol: 0, openedAt: Date.now(), nf: 1 }
  p.units += sol / mcap
  p.costSol += sol
  p.entryMcap = p.costSol / p.units   // avg fill
  s.pos[token] = p
  s.hist.push({ t: Date.now(), token, side: 'buy', sol, mcap })
  if (s.hist.length > HIST_MAX) s.hist.splice(0, s.hist.length - HIST_MAX)
  save(s)
  return p
}

// pct: 0–100 of the open paper position.
export function paperSell(token, pct, mcap) {
  if (!(mcap > 0)) throw new Error('No live price yet')
  const s = load()
  const p = token && s.pos[token]
  if (!p || !(p.units > 0)) throw new Error('No paper position')
  let f = Math.max(0, Math.min(1, (Number(pct) || 100) / 100))
  if (f >= 0.999) f = 1   // (paperaudit-20261027) ~100% sells the whole bag: no dust, nothing lost
  const units = p.units * f
  const cost = p.costSol * f
  const proceeds = units * mcap
  const pnl = proceeds - cost
  p.units -= units
  p.costSol -= cost
  s.cash += proceeds
  s.realized += pnl
  if (f >= 1 || p.units <= 0) delete s.pos[token]
  else s.pos[token] = p
  s.hist.push({ t: Date.now(), token, side: 'sell', pct: f * 100, mcap, proceeds, pnl, pnlPct: cost > 0 ? (pnl / cost) * 100 : 0 })
  if (s.hist.length > HIST_MAX) s.hist.splice(0, s.hist.length - HIST_MAX)
  save(s)
  return { pnl, proceeds }
}

export function paperHistory() { return load().hist }

// (paperaudit-20261027) Paper fills for one coin as chart trade markers.
// amount = SOL in (buy) / SOL out (sell), same as the tape's own-fill tags.
export function paperMarkers(token) {
  if (!token) return []
  return load().hist.filter(h => h.token === token && h.mcap > 0 && Number.isFinite(h.t))
    .map(h => ({ ts: h.t, mcap: h.mcap, side: h.side, amount: h.side === 'buy' ? h.sol : h.proceeds }))
}

export function paperSummary() {
  const sells = load().hist.filter(h => h.side === 'sell')
  const net = sells.reduce((a, h) => a + (h.pnl || 0), 0)
  const wins = sells.filter(h => h.pnl > 0).length
  return { sells: sells.length, wins, net }
}
